import arrayMutators from 'final-form-arrays';
import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { Alert } from '@/ui';
import { useToggle } from '../hooks';
import { useCreateOfferBlock, useOfferBlock, useUpdateOffer, useUpdateOfferBlock } from '../queries';
import { useOffersTable } from '../../Offers/queries';
import DnDFlow from '../components/DnDFlow';
import LeftAside from '../components/LeftAside';
import Navbar from '../components/Navbar';
import RightAside from '../components/RightAside';
import Criteria from '../../../components/Criteria/CriteriaFinal/Criteria';
import { UnifiedFormCriteriaPanel } from '../../../components/Criteria/CriteriaPanel';
import './OfferBlockDetails.css';
import validate from './validate';
import {
  handleCriteriaSubmit,
  handleCriteriaInitialize,
  normalizeCriteriaConfiguration,
} from '../../../utils/CriteriaHelpers';
import { useFeatures } from '../../FeatureToggles/queries';
import ZuulLoader from '../../../components/ZuulLoader/ZuulLoader';
import { useQueryClient } from '@tanstack/react-query';
import { getSequenceFlowOffersAndEdges, handleFlowOffersInit, handleOrphanedOffers } from './flowUtils';
import { Helmet } from 'react-helmet-async';
import { getPageTitle } from '../../../utils/pageTitles';

function parseOfferBlock(arr) {
  return arr.map((obj) => {
    const newObj = {};
    for (const key in obj) {
      if (obj[key] !== null && obj[key] !== '') {
        newObj[key] =
          typeof obj[key] === 'object' && Object.prototype.hasOwnProperty.call(obj[key], 'id') ? obj[key].id : obj[key];
      }
    }
    return newObj;
  });
}

function handleDomainAffiliateInit() {
  return { domainAffiliateEntries: [] };
}

function handlePreviousRegistrationInit() {
  return {
    andOrLogic: null,
    previousRegistrations: [
      {
        domains: [],
        includeExcludeAction: null,
        previousRegistrationsMin: null,
        previousRegistrationsMax: null,
      },
    ],
  };
}

function handleInternalPrePingInit() {
  return {
    andOrLogic: 'AND',
    internalPrePings: [{}],
  };
}

function handleCapSegmentInit() {
  return {
    capSegments: [{ domains: [], segmentAmount: null }],
  };
}

const OfferBlockDetails = () => {
  const { offerBlockId } = useParams();
  const navigate = useNavigate();
  const [leftAside, setLeftAside] = useToggle(true);
  const [rightAside, setRightAside] = useToggle(true);
  const [orphanedOffers, setOrphanedOffers] = useState([]);
  const [selectedElement, setSelectedElement] = useState({});
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const { data: offers, loading } = useOffersTable();
  // ZET-4257: flag-gated swap to the unified criteria panel. An offer block's criteria belong to its
  // parent Offer (offer type 9), so the panel runs with sourceType='offer'.
  const { data: offerBlockCriteriaEnabled, isLoading: loadingFeatures } = useFeatures((data) =>
    data.some((f) => f.name === 'USE_OFFER_BLOCK_CRITERIA_CONFIGURATION' && f.enabled),
  );
  const queryClient = useQueryClient();
  let {
    data: publishedSequence,
    isLoading: publishedSequenceLoading,
    isFetching: publishedSequenceFetching,
  } = useOfferBlock(offerBlockId);
  const { mutate: updateOfferBlock } = useUpdateOfferBlock();
  const { mutate: updateOffer } = useUpdateOffer();
  const { mutate: createOfferBlock } = useCreateOfferBlock();
  let isCopy = false;

  if (
    !publishedSequence ||
    !publishedSequence.offer ||
    !publishedSequence.offer.domainAffiliateConfiguration ||
    !publishedSequence.offer.previousRegistrationConfiguration ||
    !publishedSequence.offer.capSegmentConfiguration ||
    !publishedSequence.offer.internalPrePingConfiguration
  ) {
    const pubSeq = publishedSequence || {};
    const offer = pubSeq.offer || {};
    if (!offer.previousRegistrationConfiguration) {
      offer.previousRegistrationConfiguration = handlePreviousRegistrationInit();
    }
    if (!offer.capSegmentConfiguration) {
      offer.capSegmentConfiguration = handleCapSegmentInit();
    }
    if (!offer.internalPrePingConfiguration) {
      offer.internalPrePingConfiguration = handleInternalPrePingInit();
    }
    pubSeq.offer = offer;
    publishedSequence = pubSeq;
  }

  if (publishedSequence && publishedSequence.offer.domainAffiliateConfiguration == null) {
    publishedSequence.offer.domainAffiliateConfiguration = handleDomainAffiliateInit();
  }
  if (publishedSequence && publishedSequence.offer) {
    if (offerBlockCriteriaEnabled) {
      // The backend hydrates criteriaConfiguration from the criteria tables onto the block.
      publishedSequence.criteria = {
        criteriaConfiguration: JSON.stringify(publishedSequence.criteriaConfiguration ?? {}),
      };
    } else {
      const initializedCriteria = handleCriteriaInitialize(publishedSequence.offer, null);
      publishedSequence.offer = {
        ...publishedSequence.offer,
        ...initializedCriteria,
      };
    }
  }
  if (publishedSequence) {
    if (!publishedSequence.offersPublished || typeof publishedSequence.offersPublished !== 'object') {
      publishedSequence.offersPublished = {};
    }
    if (!Array.isArray(publishedSequence.offersPublished.offer_block)) {
      publishedSequence.offersPublished.offer_block = [];
    }
    if (!Array.isArray(publishedSequence.offerBlockOffers)) {
      publishedSequence.offerBlockOffers = [];
    }
  }

  if (publishedSequenceLoading || loading || publishedSequenceFetching || loadingFeatures) {
    return <ZuulLoader text={`Loading offer block...`} />;
  }

  const validateOffersSequences = (sequenceFlow) => {
    let [, offersArr] = getSequenceFlowOffersAndEdges(sequenceFlow.elements);
    let updatedOrphanedOffers = [];
    if (offersArr.length > 1) {
      updatedOrphanedOffers = handleOrphanedOffers(sequenceFlow.elements);
      setOrphanedOffers(updatedOrphanedOffers);
    }
    return updatedOrphanedOffers.length === 0;
  };

  const onSubmit = async (values) => {
    const sequenceFlow = reactFlowInstance.toObject().elements;
    const isSequenceFlowValid = validateOffersSequences({ elements: sequenceFlow });
    if (!isSequenceFlowValid) return false;
    const offersList = sequenceFlow.filter((item) => item.offerType && item.offer).map((item) => item.offer);
    // Build offer_block from the visual graph to ensure all newly added nodes are included
    const baseOfferBlock = offersList.map((offer) => ({
      offer_id: offer.id,
      container_id: null,
      layout_id: null,
      status: offer.isActive ? 1 : 0,
    }));
    // Merge any form overrides (e.g., edits from RightAside) by offer_id
    const formOfferBlock = (values?.offersPublished?.offer_block ?? []).reduce((acc, item) => {
      acc[item.offer_id] = item;
      return acc;
    }, {});
    const offer_block = baseOfferBlock.map((entry) => ({
      ...entry,
      ...(formOfferBlock[entry.offer_id] || {}),
    }));
    // handleCriteriaSubmit is required on BOTH paths: beyond the legacy criteria fields it also
    // re-serializes offer columns that useOfferBlock's select() parsed on the way in
    // (criteriaDayHourRestrictions becomes an array, criteriaUserAttributes/suppressionRules become
    // objects). Skipping it sends an array where the backend expects a String and PUT /v1/offers
    // fails with 400, which silently aborts the save chain before the criteria are written.
    // With the flag on the legacy criteria editor is not rendered, so these values round-trip
    // unchanged — the legacy columns are preserved, not cleared.
    if (values.offer) {
      values.offer = {
        ...values.offer,
        ...handleCriteriaSubmit(values.offer),
      };
    }
    // ZET-4257: with the flag on the panel's JSON is the source of truth — send it on the offer block
    // payload and stop writing the legacy offer criteria columns.
    const criteriaConfiguration =
      offerBlockCriteriaEnabled && values.criteria?.criteriaConfiguration
        ? normalizeCriteriaConfiguration(JSON.parse(values.criteria.criteriaConfiguration))
        : undefined;
    let finalSequence = {
      active: true,
      offersPublished: {
        offer_block: parseOfferBlock(offer_block),
      },
      offer: values.offer,
      ...(criteriaConfiguration ? { criteriaConfiguration } : {}),
      published: true,
      publishedAt: new Date(),
      zuulUser: JSON.parse(localStorage.getItem('userData')),
    };

    if (offerBlockId === 'create' || isCopy) {
      finalSequence.createdAt = new Date();
      finalSequence.offer = {
        ...values.offer,
        isActive: true,
        zuulUser: JSON.parse(localStorage.getItem('userData')),
      };
      if (finalSequence?.id) delete finalSequence.id;
      if (finalSequence?.offer?.id) delete finalSequence.offer.id;
      if (isCopy && values.name) finalSequence.offer.name = values.name;
      createOfferBlock(finalSequence);
      navigate('/campaigns/offerblocks');
    } else {
      finalSequence.id = parseInt(offerBlockId, 10);
      updateOffer(finalSequence.offer, {
        onSuccess: () => {
          updateOfferBlock(finalSequence, {
            onSuccess: () => {
              queryClient.invalidateQueries(['offerBlocks', offerBlockId]).then(() => {
                navigate('/campaigns/offerblocks');
              });
            },
          });
        },
      });
    }
  };

  return (
    <div className='sequence-details-container'>
      <Helmet>
        <title>{getPageTitle(location.pathname, publishedSequence.offer.name)}</title>
      </Helmet>
      <Form
        initialValues={publishedSequence}
        mutators={{
          ...arrayMutators,
        }}
        onSubmit={onSubmit}
        validate={validate}
        subscription={{
          submitting: true,
          submitFailed: true,
        }}
      >
        {({
          handleSubmit,
          form: {
            mutators: { push, remove, concat, pop },
          },
          submitting,
          submitFailed,
        }) => (
          <form onSubmit={handleSubmit} autoComplete='off' className='sequence-details-content'>
            <Navbar
              leftAside={leftAside}
              rightAside={rightAside}
              setLeftAside={setLeftAside}
              setRightAside={setRightAside}
              submitting={submitting}
              publishedSequence={publishedSequence}
              handleSubmit={handleSubmit}
              shouldErrorRender={submitFailed}
              setIsCopy={() => (isCopy = true)}
            />
            <Grid container className='sequence-main-row'>
              {leftAside && (
                <Grid size={{ xs: 12, md: 2 }} className={`toggled-${leftAside ? 'show' : 'hide'}`}>
                  <LeftAside offers={offers} />
                </Grid>
              )}
              <Grid id='sequenceDropzone' size={{ xs: 12, md: leftAside ? 7 : 9 }}>
                {orphanedOffers.length ? (
                  <Box sx={{ flexShrink: 0 }}>
                    <Alert id='invalid-offers-alert' color='info'>
                      The following offers are invalid: {orphanedOffers.map((offer) => offer.name).join(', ')}
                    </Alert>
                  </Box>
                ) : null}
                {/* Expanded, the criteria panel is taller than this column and the column hides its
                    overflow, so the bottom of the form was unreachable and the canvas below it was
                    squeezed to nothing. Capping it at half the viewport and letting it scroll keeps
                    both on screen.

                    The border and the radius belong here rather than on the card: this is the
                    element that stays put, so it is the one whose edges can be seen at every scroll
                    position. The card's own border would scroll away with it — and would double up
                    against this one at rest — so it gives way to this. */}
                <Box
                  sx={{
                    flexShrink: 0,
                    maxHeight: '50vh',
                    overflowY: 'auto',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 2,
                    '& > .MuiCard-root': { border: 0 },
                  }}
                >
                  {offerBlockCriteriaEnabled ? (
                    <UnifiedFormCriteriaPanel name='criteria' sourceType='offer' hideCapsTab />
                  ) : (
                    <Criteria push={push} pop={pop} source='offer' />
                  )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <DnDFlow
                    initialElements={handleFlowOffersInit(publishedSequence.offerBlockOffers)}
                    offers={offers}
                    selectedElement={selectedElement}
                    setSelectedElement={setSelectedElement}
                    orphanedOffers={orphanedOffers}
                    reactFlowInstance={reactFlowInstance}
                    updateOrphanedOffers={setOrphanedOffers}
                    setReactFlowInstance={setReactFlowInstance}
                    formPush={push}
                    formRemove={remove}
                    formPop={pop}
                    formConcat={concat}
                  />
                </Box>
              </Grid>
              {rightAside && (
                <Grid id='sequenceOfferMetadata' size={{ xs: 12, md: 3 }} className={`toggled-${rightAside ? 'show' : 'hide'}`}>
                  <Field required name='offersPublished.offer_block'>
                    {({ input }) => (
                      <RightAside
                        selectedElement={selectedElement}
                        selectedElementIndex={
                          input.value?.findIndex((offer) => offer.offer_id === selectedElement?.offer?.id) ?? null
                        }
                      />
                    )}
                  </Field>
                </Grid>
              )}
            </Grid>
          </form>
        )}
      </Form>
    </div>
  );
};

export default OfferBlockDetails;
